// src/pages/admin/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { 
  BarChart3, 
  Users, 
  MessageSquare, 
  FileText, 
  AlertTriangle,
  TrendingUp,
  MapPin,
  Shield,
  CheckCircle
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalReports: 0,
    pendingReports: 0,
    resolvedReports: 0,
    totalCitizens: 0,
    activeAspirants: 0,
    totalMemorandums: 0,
    smsCampaigns: 0,
    subscriptionRevenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      console.log('📊 Fetching dashboard data...');
      
      // ONLY call endpoints that actually exist
      // 1. Get aspirants (we know this works)
      const aspirantsRes = await apiService.getAspirants();
      const aspirantsData = aspirantsRes?.data || [];
      console.log('✅ Aspirants fetched:', Array.isArray(aspirantsData) ? aspirantsData.length : 0);
      
      // 2. Try to get reports - but catch error if it doesn't exist
      let reportsData: any[] = [];
      try {
        const reportsRes = await apiService.getReports({});
        reportsData = Array.isArray(reportsRes?.data?.data) ? reportsRes.data.data : 
             Array.isArray(reportsRes?.data) ? reportsRes.data : [];
        console.log('✅ Reports fetched:', reportsData.length);
      } catch (reportsError) {
        console.log('⚠️ Reports endpoint not available, using mock data');
        reportsData = []; // Use empty array if endpoint doesn't exist
      }
      
      // 3. Don't call campaigns or other non-existent endpoints
      
      // Calculate stats
      const reports = Array.isArray(reportsData) ? reportsData : [];
      const aspirants = Array.isArray(aspirantsData) ? aspirantsData : [];

      setStats({
        totalReports: reports.length,
        pendingReports: reports.filter((r: any) => r.status === 'PENDING').length,
        resolvedReports: reports.filter((r: any) => r.status === 'RESOLVED').length,
        totalCitizens: 0, // Will update when citizen endpoint is available
        activeAspirants: aspirants.length,
        totalMemorandums: 0, // Will update when memorandum endpoint is available
        smsCampaigns: 0, // Will update when campaigns endpoint is available
        subscriptionRevenue: 0, // Will update when subscription endpoint is available
      });
    } catch (error: any) {
      console.error('Failed to fetch dashboard data:', error);
      // Don't show toast for now - let it fail silently
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    trend?: string;
  }> = ({ title, value, icon, color, trend }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trend.startsWith('+') ? 'text-[#3c8d2e]' : 'text-red-600'}`}>
              {trend} from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const recentReports = [
    { id: 1, ward: 'Biashara Ward', category: 'Water', urgency: 9, time: '2 hours ago' },
    { id: 2, ward: 'Kaptembwo', category: 'Roads', urgency: 7, time: '4 hours ago' },
    { id: 3, ward: 'Naivasha East', category: 'Flooding', urgency: 10, time: '6 hours ago' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0b2045] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#0b2045]">Admin Dashboard</h1>
        <p className="text-gray-600">System overview and administration</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Reports"
          value={stats.totalReports}
          icon={<BarChart3 className="text-[#0b2045]" size={24} />}
          color="bg-[#0b2045]/10"
          trend="+12%"
        />
        <StatCard
          title="Active Citizens"
          value={stats.totalCitizens}
          icon={<Users className="text-[#3c8d2e]" size={24} />}
          color="bg-[#3c8d2e]/10"
          trend="+8%"
        />
        <StatCard
          title="Pending Actions"
          value={stats.pendingReports}
          icon={<AlertTriangle className="text-[#ca8a04]" size={24} />}
          color="bg-[#fefce8]"
          trend="+5%"
        />
        <StatCard
          title="Active Aspirants"
          value={stats.activeAspirants}
          icon={<Shield className="text-[#0b2045]" size={24} />}
          color="bg-[#0b2045]/5"
          trend="+15%"
        />
      </div>

      {/* Second Row Stats - Show placeholder for now */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Resolved Reports"
          value={stats.resolvedReports}
          icon={<CheckCircle className="text-[#3c8d2e]" size={24} />}
          color="bg-[#3c8d2e]/10"
        />
        <StatCard
          title="SMS Campaigns"
          value={stats.smsCampaigns}
          icon={<MessageSquare className="text-[#0b2045]" size={24} />}
          color="bg-[#0b2045]/10"
        />
        <StatCard
          title="Memorandums"
          value={stats.totalMemorandums}
          icon={<FileText className="text-[#ca8a04]" size={24} />}
          color="bg-[#fefce8]"
        />
        <StatCard
          title="Revenue"
          value={stats.subscriptionRevenue}
          icon={<TrendingUp className="text-[#3c8d2e]" size={24} />}
          color="bg-[#3c8d2e]/10"
          trend="+18%"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
            <Users className="text-[#0b2045] mb-2" size={24} />
            <span className="text-sm font-medium text-gray-700">Manage Users</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
            <Shield className="text-[#3c8d2e] mb-2" size={24} />
            <span className="text-sm font-medium text-gray-700">Aspirants</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
            <BarChart3 className="text-[#0b2045] mb-2" size={24} />
            <span className="text-sm font-medium text-gray-700">Reports</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
            <MessageSquare className="text-[#3c8d2e] mb-2" size={24} />
            <span className="text-sm font-medium text-gray-700">Campaigns</span>
          </button>
        </div>
      </div>

      {/* Recent Activity - Use mock data for now */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-[#0b2045]">Recent Reports</h3>
          <p className="text-sm text-gray-500">Latest issues reported by citizens</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#f4f7f6]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Ward</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Urgency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentReports.map((report) => (
                <tr key={report.id} className="hover:bg-[#f4f7f6] transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-[#0b2045]">{report.ward}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#3c8d2e]/10 text-[#0b2045]">
                      {report.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-24 h-2 rounded-full ${
                        report.urgency >= 9 ? 'bg-red-500' :
                        report.urgency >= 7 ? 'bg-[#ca8a04]' :
                        report.urgency >= 5 ? 'bg-yellow-500' : 'bg-[#3c8d2e]'
                      }`}></div>
                      <span className="ml-2 text-sm font-medium text-gray-700">{report.urgency}/10</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {report.time}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button className="text-[#3c8d2e] hover:text-[#2d7022] text-sm font-medium transition-colors">
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;